import type { CompanySummary } from '@obras/shared';
import {
  daysBetween,
  defaultTimezone,
  formatCurrency,
  formatDate,
  formatDays,
  maskCnpj,
  maskCurrency,
  parseCurrencyToCents,
  todayIn,
} from '@obras/shared';
import {
  Building2,
  CalendarClock,
  CircleAlert,
  Hammer,
  HardHat,
  Landmark,
  Mail,
  Phone,
  Plus,
  TriangleAlert,
} from 'lucide-react';
import { AnimatePresence, motion } from 'motion/react';
import { lazy, Suspense, useState, type ReactNode } from 'react';

import { EASE_OUT_QUINT } from '../../../components/motion.js';
import { Bloco, Dado, OpcaoEmCartao, Secao } from '../../../components/ui/Assistente.js';
import { Button } from '../../../components/ui/Button.js';
import { CoverImage, CoverPlaceholder } from '../../../components/ui/Cover.js';
import { Avatar, Notice } from '../../../components/ui/Feedback.js';
import { TextAreaField, TextField } from '../../../components/ui/Field.js';
import {
  MultiSelect,
  SingleSelect,
  type MultiSelectOption,
} from '../../../components/ui/MultiSelect.js';
import { cn } from '../../../lib/cn.js';
import { CapaDaObra } from '../CapaDaObra.js';
import { coordOrNull, type FormularioDaObra } from '../formularioDaObra.js';
import type { CapaGuardada } from '../rascunhoDaObra.js';
import { ROTULO_DA_ETAPA, type EtapaDaObraId } from './etapas.js';

const ICON = { strokeWidth: 1.5 } as const;

/**
 * O mapa entra por carregamento tardio: Leaflet e a folha de estilo dele só
 * fazem falta na etapa de localização, e embutidos no pacote principal seriam
 * baixados por quem só abre a lista de obras.
 */
const MapaLocal = lazy(async () => ({
  default: (await import('../../../components/ui/MapaLocal.js')).MapaLocal,
}));

/** O que toda etapa recebe: o formulário, como mudá-lo e o erro de cada campo. */
export interface PropsDaEtapa {
  form: FormularioDaObra;
  definir: <K extends keyof FormularioDaObra>(campo: K, valor: FormularioDaObra[K]) => void;
  /** O erro visível de um campo, já no formato que os campos esperam. */
  erroDe: (campo: string) => { error?: string };
}

// ──────────────────────────────────────────────────────────────── peças

/**
 * Um seletor com rótulo à vista. O `<SingleSelect>` só tem nome acessível; aqui
 * ele ganha o rótulo em cima, a dica (ou o erro) embaixo e, quando há escolha,
 * o "Limpar" ao lado do rótulo — no mesmo desenho dos campos de texto.
 */
function CampoComSeletor({
  rotulo,
  dica,
  erro,
  onLimpar,
  children,
}: {
  rotulo: string;
  dica?: string | undefined;
  erro?: string | undefined;
  onLimpar?: (() => void) | undefined;
  children: ReactNode;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <span className="flex items-baseline justify-between gap-3">
        <span className="text-[13px] font-medium text-ink">{rotulo}</span>
        {onLimpar ? (
          <button
            type="button"
            onClick={onLimpar}
            className="text-[12px] font-medium text-ink-3 transition-colors hover:text-ink"
          >
            Limpar
          </button>
        ) : null}
      </span>
      {children}
      {erro ? (
        <p role="alert" className="flex items-center gap-1.5 text-[13px] text-danger">
          <CircleAlert size={14} strokeWidth={1.75} className="shrink-0" aria-hidden />
          {erro}
        </p>
      ) : dica ? (
        <p className="text-[13px] text-ink-3">{dica}</p>
      ) : null}
    </div>
  );
}

/** Um rótulo de grupo de escolha, no desenho do rótulo de campo. */
function RotuloDeGrupo({ children, dica }: { children: ReactNode; dica?: string | undefined }) {
  return (
    <div className="mb-2.5">
      <p className="text-[13px] font-medium text-ink">{children}</p>
      {dica ? <p className="mt-0.5 text-[13px] text-ink-3">{dica}</p> : null}
    </div>
  );
}

/** Aparece e some deslizando um fio — o que surge enquanto se preenche. */
function Surge({ chave, children }: { chave: string; children: ReactNode }) {
  return (
    <AnimatePresence initial={false} mode="popLayout">
      <motion.div
        key={chave}
        initial={{ opacity: 0, y: -4 }}
        animate={{ opacity: 1, y: 0, transition: { duration: 0.24, ease: EASE_OUT_QUINT } }}
        exit={{ opacity: 0, transition: { duration: 0.1 } }}
      >
        {children}
      </motion.div>
    </AnimatePresence>
  );
}

// ───────────────────────────────────────────────────────── identificação

export function EtapaIdentificacao({
  form,
  definir,
  erroDe,
  secretarias,
  carregandoSecretarias,
  podeCadastrarSecretaria,
  onNovaSecretaria,
}: PropsDaEtapa & {
  secretarias: readonly MultiSelectOption[];
  carregandoSecretarias: boolean;
  podeCadastrarSecretaria: boolean;
  onNovaSecretaria: () => void;
}) {
  return (
    <>
      <TextField
        label="Denominação da obra"
        required
        maxLength={200}
        data-foco-da-etapa
        placeholder="Construção da Unidade Básica de Saúde do Bairro Jardim"
        hint="Como a obra aparece na lista, nos relatórios e nos documentos."
        value={form.name}
        onChange={(evento) => definir('name', evento.target.value)}
        {...erroDe('name')}
      />

      <div>
        <RotuloDeGrupo>Tipo de intervenção</RotuloDeGrupo>
        <div
          role="radiogroup"
          aria-label="Tipo de intervenção"
          className="grid gap-2 sm:grid-cols-2"
        >
          <OpcaoEmCartao
            ativa={!form.isReform}
            onEscolher={() => definir('isReform', false)}
            icone={<HardHat {...ICON} size={18} aria-hidden />}
            titulo="Construção nova"
            descricao="Edificação ou infraestrutura que ainda não existe."
          />
          <OpcaoEmCartao
            ativa={form.isReform}
            onEscolher={() => definir('isReform', true)}
            icone={<Hammer {...ICON} size={18} aria-hidden />}
            titulo="Reforma ou ampliação"
            descricao="Intervenção numa edificação que já existe."
          />
        </div>
      </div>

      <TextAreaField
        label="Objeto"
        rows={4}
        hint="Transcreva o objeto como está redigido no contrato."
        value={form.description}
        onChange={(evento) => definir('description', evento.target.value)}
        {...erroDe('description')}
      />

      {/*
       * Sem campo de situação: a obra cadastrada nasce em andamento, e mudar a
       * situação (paralisada, concluída) é coisa da vida dela, feita na edição.
       */}
      <CampoComSeletor
        rotulo="Secretaria demandante"
        dica="Órgão interno que solicitou a obra e responde por ela."
        erro={erroDe('secretariaId').error}
        onLimpar={form.secretariaId ? () => definir('secretariaId', '') : undefined}
      >
        <div className="flex gap-2">
          <SingleSelect
            className="min-w-0 flex-1"
            label="Secretaria demandante"
            searchable
            placeholder={carregandoSecretarias ? 'Carregando…' : 'Buscar por nome ou sigla'}
            options={secretarias}
            value={form.secretariaId || null}
            onChange={(valor) => definir('secretariaId', valor)}
          />
          {podeCadastrarSecretaria ? (
            <Button
              icon={<Landmark {...ICON} size={15} />}
              onClick={onNovaSecretaria}
              title="Cadastrar uma secretaria que ainda não está no sistema"
            >
              Cadastrar
            </Button>
          ) : null}
        </div>
      </CampoComSeletor>
    </>
  );
}

// ────────────────────────────────────────────────────────────── contrato

/** A empresa escolhida, por extenso: o que confirma que é a certa. */
function FichaDaEmpresa({ empresa }: { empresa: CompanySummary }) {
  return (
    <div className="flex items-start gap-3.5 border border-line bg-surface-2 p-4">
      <span className="grid size-10 shrink-0 place-items-center bg-sunken text-ink-2">
        <Building2 {...ICON} size={18} aria-hidden />
      </span>
      <div className="min-w-0 flex-1">
        <p className="text-[14px] font-semibold tracking-tight text-ink">
          {empresa.tradeName ?? empresa.legalName}
        </p>
        {empresa.tradeName ? <p className="text-[13px] text-ink-3">{empresa.legalName}</p> : null}
        <p className="mt-2 flex flex-wrap gap-x-5 gap-y-1 text-[12px] text-ink-3">
          {empresa.cnpj ? (
            <span className="tabular font-mono">CNPJ {maskCnpj(empresa.cnpj)}</span>
          ) : null}
          {empresa.phone ? (
            <span className="flex items-center gap-1.5">
              <Phone {...ICON} size={13} aria-hidden />
              {empresa.phone}
            </span>
          ) : null}
          {empresa.email ? (
            <span className="flex items-center gap-1.5">
              <Mail {...ICON} size={13} aria-hidden />
              {empresa.email}
            </span>
          ) : null}
        </p>
      </div>
    </div>
  );
}

export function EtapaContrato({
  form,
  definir,
  erroDe,
  empresas,
  empresaEscolhida,
  carregandoEmpresas,
  podeCadastrarEmpresa,
  onNovaEmpresa,
}: PropsDaEtapa & {
  empresas: readonly MultiSelectOption[];
  empresaEscolhida: CompanySummary | null;
  carregandoEmpresas: boolean;
  podeCadastrarEmpresa: boolean;
  onNovaEmpresa: () => void;
}) {
  return (
    <>
      <div className="grid gap-4 sm:grid-cols-2">
        <TextField
          label="Número do contrato"
          required
          mono
          maxLength={40}
          placeholder="056/2026"
          hint="Como consta no instrumento assinado."
          value={form.contractNumber}
          onChange={(evento) => definir('contractNumber', evento.target.value)}
          {...erroDe('contractNumber')}
        />
        {/*
         * A máscara entra a cada tecla, da direita para a esquerda. Formatar só
         * ao sair do campo deixava o número ambíguo justamente enquanto a pessoa
         * o conferia.
         */}
        <TextField
          label="Valor inicial contratado"
          required
          mono
          inputMode="numeric"
          placeholder="R$ 0,00"
          hint="Aditivos e supressões entram depois, pelos relatórios."
          value={form.initialValue}
          onChange={(evento) => definir('initialValue', maskCurrency(evento.target.value))}
          {...erroDe('initialValueCents')}
        />
      </div>

      <Secao titulo="Empresa contratada">
        <CampoComSeletor
          rotulo="Empresa"
          dica="Pessoa jurídica responsável pela execução. Pode ser vinculada depois."
          erro={erroDe('companyId').error}
          onLimpar={form.companyId ? () => definir('companyId', '') : undefined}
        >
          <SingleSelect
            label="Empresa contratada"
            searchable
            placeholder={carregandoEmpresas ? 'Carregando empresas…' : 'Buscar por nome ou CNPJ'}
            options={empresas}
            value={form.companyId || null}
            onChange={(valor) => definir('companyId', valor)}
          />
        </CampoComSeletor>

        {empresaEscolhida ? (
          <Surge chave={empresaEscolhida.id}>
            <FichaDaEmpresa empresa={empresaEscolhida} />
          </Surge>
        ) : (
          /*
           * Cadastrar a empresa daqui, sem sair da página: a licitação termina e
           * a obra é cadastrada no mesmo dia, com a empresa ainda fora do
           * sistema.
           */
          <div className="border border-dashed border-line-2 bg-surface-2 px-4 py-3.5">
            <p className="text-[13px] font-medium text-ink">Empresa ainda não cadastrada?</p>
            {podeCadastrarEmpresa ? (
              <>
                <p className="mt-0.5 text-[13px] text-ink-3">
                  Cadastre sem sair daqui. A partir do CNPJ, o sistema busca razão social, endereço
                  e telefone na base da Receita Federal.
                </p>
                <Button
                  size="sm"
                  className="mt-3"
                  icon={<Plus {...ICON} size={14} />}
                  onClick={onNovaEmpresa}
                >
                  Cadastrar nova empresa
                </Button>
              </>
            ) : (
              <p className="mt-0.5 text-[13px] text-ink-3">
                O cadastro de empresas depende do acesso "Gerenciar empresas". A obra pode ser
                criada agora e vinculada à empresa depois.
              </p>
            )}
          </div>
        )}
      </Secao>
    </>
  );
}

// ──────────────────────────────────────────────────────────────── prazos

/** Período completo e em ordem, ou nada. */
function periodo(inicio: string, fim: string): { inicio: string; fim: string } | null {
  return inicio && fim && fim >= inicio ? { inicio, fim } : null;
}

/** "365 dias · cerca de 12 meses": o número exato e o que se fala em reunião. */
function duracao(inicio: string, fim: string): string {
  const dias = daysBetween(inicio, fim);
  const meses = Math.round(dias / 30.44);
  return meses >= 2 ? `${formatDays(dias)} · cerca de ${meses} meses` : formatDays(dias);
}

/** O tamanho do período, assim que as duas datas estão certas. */
function Duracao({ rotulo, inicio, fim }: { rotulo: string; inicio: string; fim: string }) {
  const valido = periodo(inicio, fim);
  if (!valido) return null;

  return (
    <Surge chave={`${inicio}-${fim}`}>
      <p className="flex items-center gap-2 text-[13px] text-ink-2">
        <CalendarClock {...ICON} size={15} className="text-ink-3" aria-hidden />
        {rotulo} de <span className="tabular font-medium text-ink">{duracao(inicio, fim)}</span>
      </p>
    </Surge>
  );
}

/**
 * Os dois períodos numa régua comum: o do contrato e o da ordem de serviço, e
 * o dia de hoje. É onde se vê, sem conta, a OS que passa do contrato ou que
 * começa antes do início do contrato — e o aviso diz isso por escrito, sem impedir.
 */
function LinhaDosPrazos({ form }: { form: FormularioDaObra }) {
  const contrato = periodo(form.contractStart, form.contractEnd);
  const os = periodo(form.osStart, form.osEnd);
  const linhas = [
    contrato ? { rotulo: 'Contrato', ...contrato, cor: 'bg-ink-3/60' } : null,
    os ? { rotulo: 'Ordem de serviço', ...os, cor: 'bg-primary' } : null,
  ].filter((linha): linha is NonNullable<typeof linha> => linha !== null);

  if (linhas.length === 0) return null;

  const comeco = linhas.reduce(
    (menor, linha) => (linha.inicio < menor ? linha.inicio : menor),
    linhas[0]!.inicio,
  );
  const termino = linhas.reduce(
    (maior, linha) => (linha.fim > maior ? linha.fim : maior),
    linhas[0]!.fim,
  );
  const total = Math.max(daysBetween(comeco, termino), 1);
  const posicao = (data: string): number => (daysBetween(comeco, data) / total) * 100;

  const hoje = todayIn(defaultTimezone());
  const hojeNaRegua = hoje >= comeco && hoje <= termino ? posicao(hoje) : null;

  const avisos: string[] = [];
  if (contrato && os) {
    if (os.inicio < contrato.inicio) {
      avisos.push('A ordem de serviço começa antes do início do contrato.');
    }
    if (os.fim > contrato.fim) {
      avisos.push('O término previsto da OS passa do encerramento do contrato.');
    }
  }

  return (
    <Surge chave="linha-dos-prazos">
      <div className="border border-line bg-surface-2 p-4">
        <p className="text-[11px] font-medium tracking-[0.06em] text-ink-3 uppercase">
          Linha do tempo
        </p>
        <div className="mt-3 flex flex-col gap-3.5">
          {linhas.map((linha) => (
            <div key={linha.rotulo}>
              <div className="flex items-baseline justify-between gap-3 text-[12px]">
                <span className="font-medium text-ink-2">{linha.rotulo}</span>
                <span className="tabular text-ink-3">
                  {formatDate(linha.inicio)} a {formatDate(linha.fim)}
                </span>
              </div>
              <div className="relative mt-1.5 h-2 bg-sunken">
                <motion.span
                  className={cn('absolute inset-y-0', linha.cor)}
                  initial={false}
                  animate={{
                    left: `${posicao(linha.inicio)}%`,
                    width: `${Math.max(posicao(linha.fim) - posicao(linha.inicio), 0.8)}%`,
                  }}
                  transition={{ duration: 0.5, ease: EASE_OUT_QUINT }}
                />
                {hojeNaRegua !== null ? (
                  <span
                    aria-hidden
                    className="absolute -inset-y-1 w-px bg-ink"
                    style={{ left: `${hojeNaRegua}%` }}
                  />
                ) : null}
              </div>
            </div>
          ))}
        </div>
        {hojeNaRegua !== null ? (
          <p className="mt-2.5 flex items-center gap-1.5 text-[12px] text-ink-3">
            <span aria-hidden className="h-3 w-px bg-ink" />
            Hoje, {formatDate(hoje)}
          </p>
        ) : null}
        {avisos.map((aviso) => (
          <p key={aviso} className="mt-2.5 flex items-start gap-1.5 text-[12px] text-warning">
            <TriangleAlert {...ICON} size={14} className="mt-px shrink-0" aria-hidden />
            {aviso}
          </p>
        ))}
      </div>
    </Surge>
  );
}

export function EtapaPrazos({ form, definir, erroDe }: PropsDaEtapa) {
  return (
    <>
      <Secao titulo="Vigência do contrato">
        <div className="grid gap-4 sm:grid-cols-2">
          <TextField
            label="Início"
            type="date"
            mono
            value={form.contractStart}
            onChange={(evento) => definir('contractStart', evento.target.value)}
            {...erroDe('contractStart')}
          />
          <TextField
            label="Encerramento"
            type="date"
            mono
            value={form.contractEnd}
            onChange={(evento) => definir('contractEnd', evento.target.value)}
            {...erroDe('contractEnd')}
          />
        </div>
        <Duracao rotulo="Vigência" inicio={form.contractStart} fim={form.contractEnd} />
      </Secao>

      <Secao titulo="Ordem de serviço">
        <div className="grid gap-4 sm:grid-cols-2">
          <TextField
            label="Emissão"
            type="date"
            mono
            hint="Marco inicial da contagem do prazo de execução."
            value={form.osStart}
            onChange={(evento) => definir('osStart', evento.target.value)}
            {...erroDe('osStart')}
          />
          <TextField
            label="Término previsto"
            type="date"
            mono
            value={form.osEnd}
            onChange={(evento) => definir('osEnd', evento.target.value)}
            {...erroDe('osEnd')}
          />
        </div>
        <Duracao rotulo="Prazo de execução" inicio={form.osStart} fim={form.osEnd} />
      </Secao>

      <LinhaDosPrazos form={form} />
    </>
  );
}

// ────────────────────────────────────────────────────────── fiscalização

/** Os intervalos de vistoria mais usados; o resto vai por "Outro intervalo". */
const INTERVALOS = [
  { dias: '7', titulo: 'Semanal', descricao: 'a cada 7 dias' },
  { dias: '15', titulo: 'Quinzenal', descricao: 'a cada 15 dias' },
  { dias: '30', titulo: 'Mensal', descricao: 'a cada 30 dias' },
] as const;

const EH_INTERVALO_PRONTO = (valor: string): boolean =>
  INTERVALOS.some((intervalo) => intervalo.dias === valor);

export function EtapaFiscalizacao({
  form,
  definir,
  erroDe,
  fiscais,
  carregandoFiscais,
  podeDesignar,
  fiscalFixo,
  notaDoFiscal,
  membrosDaEquipe,
  equipe,
  onEquipe,
  intervaloPadrao,
}: PropsDaEtapa & {
  fiscais: readonly MultiSelectOption[];
  carregandoFiscais: boolean;
  /** Só a gestão escolhe o fiscal; o fiscal que cadastra assume a obra. */
  podeDesignar: boolean;
  fiscalFixo: string | null;
  notaDoFiscal: string;
  /** Quem pode entrar na equipe: o órgão, sem o responsável. */
  membrosDaEquipe: readonly MultiSelectOption[];
  equipe: readonly string[];
  onEquipe: (equipe: string[]) => void;
  intervaloPadrao: number;
}) {
  /*
   * "Outro intervalo" é escolha, não só valor: escolhido, mostra o campo, mesmo
   * que o número digitado coincida com um dos prontos. Volta-se à etapa e o
   * estado é refeito pelo valor — fora dos prontos, é "outro".
   */
  const [outro, setOutro] = useState(() => !EH_INTERVALO_PRONTO(form.visitIntervalDays));
  const dias = Number(form.visitIntervalDays);
  const intervaloValido = Number.isInteger(dias) && dias >= 1 && dias <= 365;

  return (
    <>
      {/*
       * A equipe: o responsável — quem cadastra, de saída — e quantos mais
       * acompanharem a obra, como na aba Equipe. Responsável é vaga única; o
       * resto entra como auxiliar.
       */}
      <Secao titulo="Equipe da obra">
        {podeDesignar ? (
          <CampoComSeletor
            rotulo="Fiscal responsável"
            dica="Quem cadastra a obra já vem como responsável. A gestão pode designar outro servidor."
            erro={erroDe('responsibleFiscalId').error}
            onLimpar={
              form.responsibleFiscalId ? () => definir('responsibleFiscalId', '') : undefined
            }
          >
            <SingleSelect
              label="Fiscal responsável"
              searchable
              placeholder={carregandoFiscais ? 'Carregando…' : 'Nenhum servidor designado'}
              options={fiscais}
              value={form.responsibleFiscalId || null}
              onChange={(valor) => definir('responsibleFiscalId', valor)}
            />
          </CampoComSeletor>
        ) : (
          <div className="flex flex-col gap-1.5">
            <span className="text-[13px] font-medium text-ink">Fiscal responsável</span>
            <div className="flex items-center gap-3.5 border border-line bg-surface-2 p-4">
              {fiscalFixo ? (
                <Avatar name={fiscalFixo} size={36} />
              ) : (
                <span className="grid size-9 place-items-center bg-sunken text-ink-3">
                  <HardHat {...ICON} size={16} aria-hidden />
                </span>
              )}
              <div className="min-w-0">
                <p className="text-[14px] font-semibold tracking-tight text-ink">
                  {fiscalFixo ?? 'Nenhum fiscal designado'}
                </p>
                <p className="text-[13px] text-ink-3">{notaDoFiscal}</p>
              </div>
            </div>
          </div>
        )}

        <CampoComSeletor
          rotulo="Equipe"
          dica="Os demais servidores que acompanham a obra. Entram como auxiliares, como na aba Equipe."
          erro={erroDe('teamMemberIds').error}
          onLimpar={equipe.length > 0 ? () => onEquipe([]) : undefined}
        >
          <MultiSelect
            label={carregandoFiscais ? 'Carregando…' : 'Escolher servidores do órgão'}
            variant="field"
            searchable
            options={membrosDaEquipe}
            selected={equipe}
            onChange={onEquipe}
          />
        </CampoComSeletor>
      </Secao>

      <Secao titulo="Periodicidade das vistorias">
        <div
          role="radiogroup"
          aria-label="Periodicidade das vistorias"
          className="grid grid-cols-2 gap-2 sm:grid-cols-4"
        >
          {INTERVALOS.map((intervalo) => (
            <OpcaoEmCartao
              key={intervalo.dias}
              ativa={!outro && form.visitIntervalDays === intervalo.dias}
              onEscolher={() => {
                setOutro(false);
                definir('visitIntervalDays', intervalo.dias);
              }}
              titulo={intervalo.titulo}
              descricao={intervalo.descricao}
            />
          ))}
          <OpcaoEmCartao
            ativa={outro}
            onEscolher={() => setOutro(true)}
            titulo="Outro"
            descricao="intervalo próprio"
          />
        </div>

        {outro ? (
          <Surge chave="outro-intervalo">
            <div className="max-w-[240px]">
              <TextField
                label="Intervalo, em dias"
                type="number"
                min={1}
                max={365}
                mono
                placeholder={String(intervaloPadrao)}
                hint="De 1 a 365."
                value={form.visitIntervalDays}
                onChange={(evento) => definir('visitIntervalDays', evento.target.value)}
                {...erroDe('visitIntervalDays')}
              />
            </div>
          </Surge>
        ) : null}

        <Notice icon={CalendarClock}>
          {intervaloValido ? (
            <>
              Sem relatório de visita a cada{' '}
              <strong className="font-semibold">{formatDays(dias)}</strong>, a obra aparece como
              visita vencida no painel.
            </>
          ) : (
            <>Sem intervalo informado, vale o padrão do órgão.</>
          )}{' '}
          O padrão do órgão é de {formatDays(intervaloPadrao)}.
        </Notice>
      </Secao>
    </>
  );
}

// ──────────────────────────────────────────────────────────── localização

export function EtapaLocalizacao({
  form,
  definir,
  erroDe,
  buscandoEndereco,
  onMarcar,
}: PropsDaEtapa & {
  buscandoEndereco: boolean;
  onMarcar: (latitude: number, longitude: number) => void;
}) {
  const lat = coordOrNull(form.latitude);
  const lng = coordOrNull(form.longitude);

  return (
    <>
      <div>
        <Suspense
          fallback={
            <div className="grid h-80 w-full place-items-center bg-sunken text-[13px] text-ink-3">
              Carregando o mapa…
            </div>
          }
        >
          <MapaLocal latitude={lat} longitude={lng} onChange={onMarcar} />
        </Suspense>
      </div>

      <Secao titulo="Endereço">
        <TextField
          label="Logradouro"
          placeholder="Avenida Ari Mendes da Silva, s/nº"
          hint={
            buscandoEndereco
              ? 'Consultando o endereço do ponto marcado…'
              : 'Preenchido a partir do mapa quando está vazio.'
          }
          value={form.address}
          onChange={(evento) => definir('address', evento.target.value)}
          {...erroDe('address')}
        />
        <div className="grid gap-4 sm:grid-cols-[1fr_120px]">
          <TextField
            label="Município"
            value={form.city}
            onChange={(evento) => definir('city', evento.target.value)}
            {...erroDe('city')}
          />
          <TextField
            label="UF"
            maxLength={2}
            mono
            placeholder="MT"
            value={form.state}
            onChange={(evento) => definir('state', evento.target.value.toUpperCase())}
            {...erroDe('state')}
          />
        </div>
      </Secao>

      <Secao titulo="Coordenadas">
        <div className="grid gap-4 sm:grid-cols-2">
          <TextField
            label="Latitude"
            mono
            inputMode="decimal"
            placeholder="-10.178211"
            hint="Preenchida pelo mapa, ou digitada."
            value={form.latitude}
            onChange={(evento) => definir('latitude', evento.target.value)}
            {...erroDe('latitude')}
          />
          <TextField
            label="Longitude"
            mono
            inputMode="decimal"
            placeholder="-59.445292"
            value={form.longitude}
            onChange={(evento) => definir('longitude', evento.target.value)}
            {...erroDe('longitude')}
          />
        </div>
      </Secao>
    </>
  );
}

// ──────────────────────────────────────────────────────────────── capa

/**
 * Como a obra vai aparecer na lista: o cartão de obra, montado com o que já foi
 * preenchido. Muda enquanto se preenche — o nome, o contrato, a capa — e é o
 * que torna a capa uma escolha, e não um campo a mais.
 */
function PreviaDoCartao({
  form,
  capa,
  fiscal,
}: {
  form: FormularioDaObra;
  capa: CapaGuardada | null;
  fiscal: string | null;
}) {
  return (
    <div aria-hidden className="flex flex-col">
      <div className="flex flex-col overflow-hidden border border-b-0 border-line bg-surface">
        <div className="relative aspect-[16/9] shrink-0 overflow-hidden">
          {capa ? <CoverImage src={capa.url} /> : <CoverPlaceholder busy={false} />}
        </div>
        <div className="flex flex-col gap-2.5 px-4 pt-3.5 pb-3.5">
          <h3 className="line-clamp-2 h-10 text-[15px] leading-5 font-semibold tracking-tight">
            {form.name.trim() || 'Denominação da obra'}
          </h3>
          <div className="flex h-[18px] items-center gap-3 text-[12px] text-ink-3">
            <span className="shrink-0 font-mono">
              {form.contractNumber.trim() || 'sem contrato'}
            </span>
            {fiscal ? (
              <span className="ml-auto flex min-w-0 items-center gap-1.5">
                <Avatar name={fiscal} size={18} />
                <span className="truncate">{fiscal}</span>
              </span>
            ) : (
              <span className="ml-auto">sem fiscal</span>
            )}
          </div>
        </div>
        <dl className="grid grid-cols-2 divide-x divide-line border-t border-line">
          {['Execução', 'Prazo decorrido'].map((rotulo) => (
            <div key={rotulo} className="px-4 py-2.5">
              <dt className="text-[10px] font-medium tracking-[0.06em] text-ink-3 uppercase">
                {rotulo}
              </dt>
              <dd className="tabular mt-0.5 text-[15px]">0%</dd>
            </div>
          ))}
        </dl>
      </div>
      <span className="h-[3px] bg-line-2" />
    </div>
  );
}

export function EtapaCapa({
  form,
  capa,
  fiscal,
  onCapa,
}: Pick<PropsDaEtapa, 'form'> & {
  capa: CapaGuardada | null;
  fiscal: string | null;
  onCapa: (escolhida: { mediaId: string; url: string } | null) => void;
}) {
  return (
    <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_300px] lg:items-start">
      <CapaDaObra atual={capa} onChange={onCapa} semRotulo />
      <div>
        <p className="mb-2 text-[11px] font-medium tracking-[0.06em] text-ink-3 uppercase">
          Prévia na lista de obras
        </p>
        <PreviaDoCartao form={form} capa={capa} fiscal={fiscal} />
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────── revisão

const NAO_INFORMADO = <span className="text-ink-3">Não informado</span>;

/** "05/01/2026 a 31/12/2026 · 360 dias", ou o pedaço que houver. */
function textoDoPeriodo(inicio: string, fim: string, rotuloInicio: string): ReactNode {
  if (inicio && fim) {
    return (
      <span className="tabular">
        {formatDate(inicio)} a {formatDate(fim)}
        {fim >= inicio ? <span className="text-ink-3"> · {duracao(inicio, fim)}</span> : null}
      </span>
    );
  }
  if (inicio) return <span className="tabular">{`${rotuloInicio} em ${formatDate(inicio)}`}</span>;
  if (fim) return <span className="tabular">{`Até ${formatDate(fim)}`}</span>;
  return NAO_INFORMADO;
}

export function EtapaRevisao({
  form,
  capa,
  nomes,
  pendencias,
  onAlterar,
}: {
  form: FormularioDaObra;
  capa: CapaGuardada | null;
  nomes: {
    secretaria: string | null;
    empresa: CompanySummary | null;
    fiscal: string | null;
    /** Os auxiliares, pelo nome. */
    equipe: readonly string[];
  };
  /** Etapas com o que falta: a revisão aponta, e "Criar obra" leva até a primeira. */
  pendencias: readonly EtapaDaObraId[];
  onAlterar: (etapa: EtapaDaObraId) => void;
}) {
  const valor = parseCurrencyToCents(form.initialValue) ?? 0;
  const lat = coordOrNull(form.latitude);
  const lng = coordOrNull(form.longitude);
  const endereco = [
    form.address.trim(),
    [form.city.trim(), form.state.trim()].filter(Boolean).join('/'),
  ]
    .filter(Boolean)
    .join(' — ');

  return (
    <>
      {pendencias.length > 0 ? (
        <Notice tone="warning" icon={TriangleAlert}>
          <span className="flex flex-col gap-1.5">
            <span>Antes de criar, falta completar:</span>
            <span className="flex flex-wrap gap-2">
              {pendencias.map((etapa) => (
                <button
                  key={etapa}
                  type="button"
                  onClick={() => onAlterar(etapa)}
                  className="font-semibold underline underline-offset-2"
                >
                  {ROTULO_DA_ETAPA[etapa]}
                </button>
              ))}
            </span>
          </span>
        </Notice>
      ) : null}

      <Bloco titulo="Identificação" onAlterar={() => onAlterar('identificacao')}>
        <dl className="grid gap-x-6 gap-y-3 sm:grid-cols-2">
          <Dado rotulo="Denominação" largo>
            <span className="font-medium">{form.name.trim()}</span>
          </Dado>
          <Dado rotulo="Tipo de intervenção">
            {form.isReform ? 'Reforma ou ampliação' : 'Construção nova'}
          </Dado>
          <Dado rotulo="Secretaria demandante" largo>
            {nomes.secretaria ?? <span className="text-ink-3">Não informada</span>}
          </Dado>
          <Dado rotulo="Objeto" largo>
            {form.description.trim() ? (
              <span className="line-clamp-4 whitespace-pre-line text-ink-2">
                {form.description.trim()}
              </span>
            ) : (
              NAO_INFORMADO
            )}
          </Dado>
        </dl>
      </Bloco>

      <Bloco titulo="Contrato" onAlterar={() => onAlterar('contrato')}>
        <dl className="grid gap-x-6 gap-y-3 sm:grid-cols-2">
          <Dado rotulo="Número do contrato">
            <span className="font-mono">{form.contractNumber.trim()}</span>
          </Dado>
          <Dado rotulo="Valor inicial contratado">
            <span className="tabular font-medium">{formatCurrency(valor)}</span>
          </Dado>
          <Dado rotulo="Empresa contratada" largo>
            {nomes.empresa ? (
              <>
                {nomes.empresa.tradeName ?? nomes.empresa.legalName}
                {nomes.empresa.cnpj ? (
                  <span className="tabular ml-2 font-mono text-[12px] text-ink-3">
                    {maskCnpj(nomes.empresa.cnpj)}
                  </span>
                ) : null}
              </>
            ) : (
              <span className="text-ink-3">Não informada</span>
            )}
          </Dado>
        </dl>
      </Bloco>

      <Bloco titulo="Prazos" onAlterar={() => onAlterar('prazos')}>
        <dl className="grid gap-x-6 gap-y-3 sm:grid-cols-2">
          <Dado rotulo="Vigência do contrato">
            {textoDoPeriodo(form.contractStart, form.contractEnd, 'Início')}
          </Dado>
          <Dado rotulo="Ordem de serviço">
            {textoDoPeriodo(form.osStart, form.osEnd, 'Emitida')}
          </Dado>
        </dl>
      </Bloco>

      <Bloco titulo="Fiscalização" onAlterar={() => onAlterar('fiscalizacao')}>
        <dl className="grid gap-x-6 gap-y-3 sm:grid-cols-2">
          <Dado rotulo="Fiscal responsável">
            {nomes.fiscal ?? <span className="text-ink-3">Nenhum designado</span>}
          </Dado>
          <Dado rotulo="Vistorias">
            {form.visitIntervalDays.trim()
              ? `A cada ${formatDays(Number(form.visitIntervalDays))}`
              : 'Padrão do órgão'}
          </Dado>
          <Dado rotulo="Equipe" largo>
            {nomes.equipe.length > 0 ? (
              nomes.equipe.join(', ')
            ) : (
              <span className="text-ink-3">Só o responsável</span>
            )}
          </Dado>
        </dl>
      </Bloco>

      <Bloco titulo="Localização" onAlterar={() => onAlterar('localizacao')}>
        <dl className="grid gap-x-6 gap-y-3 sm:grid-cols-2">
          <Dado rotulo="Endereço" largo>
            {endereco || NAO_INFORMADO}
          </Dado>
          <Dado rotulo="Coordenadas" largo>
            {lat !== null && lng !== null ? (
              <span className="tabular font-mono text-[13px]">
                {lat}, {lng}
              </span>
            ) : (
              <span className="text-ink-3">Não marcadas</span>
            )}
          </Dado>
        </dl>
      </Bloco>

      <Bloco titulo="Capa" onAlterar={() => onAlterar('capa')}>
        {capa ? (
          <img src={capa.url} alt="" className="aspect-[16/9] w-48 object-cover" />
        ) : (
          <p className="text-[13px] text-ink-3">
            Sem imagem. Até haver uma, a lista usa a primeira foto do relatório mais recente.
          </p>
        )}
      </Bloco>

      <Notice icon={Building2}>
        Ao criar, a obra entra na lista de obras
        {nomes.fiscal ? `, sob a fiscalização de ${nomes.fiscal},` : ''} e o livro-razão começa com
        o valor inicial de <strong className="font-semibold">{formatCurrency(valor)}</strong>.
      </Notice>
    </>
  );
}
